import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { User, Lock, ArrowRight, AlertCircle } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      setError((err as Error).message ?? 'Login failed');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grid-bg">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex w-16 h-16 rounded bg-neon-cyan/10 border border-neon-cyan items-center justify-center mb-4 shadow-neon-cyan">
            <span className="font-display font-black text-2xl text-neon-cyan">RJ</span>
          </div>
          <h1 className="font-display text-3xl font-black tracking-widest">
            RJ <span className="text-neon-cyan">TRACKING</span>
          </h1>
          <p className="text-text-secondary text-sm mt-2 font-mono">
            // affiliate traffic optimization platform
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="cyber-panel p-8 space-y-5 animate-slide-up">
          <div className="text-center mb-2">
            <div className="text-[10px] font-display uppercase tracking-widest text-text-tertiary">
              ━━━ Authentication Required ━━━
            </div>
          </div>

          <Input
            label="Username"
            icon={<User size={16} />}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            required
          />

          <Input
            label="Password"
            type="password"
            icon={<Lock size={16} />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 border border-neon-red/40 bg-neon-red/5 rounded text-xs text-neon-red font-mono">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            loading={loading}
            className="w-full"
            icon={<ArrowRight size={16} />}
          >
            Access System
          </Button>

          <div className="text-center text-[10px] text-text-tertiary font-mono uppercase tracking-wider pt-2">
            © 2026 RJ Network · v0.1
          </div>
        </form>

        <p className="text-center text-xs text-text-tertiary mt-6 font-mono">
          Need access? Contact your administrator.
        </p>
      </div>
    </div>
  );
}
